import { decodeTime } from "ulid";
/**
 * SoundboardSound Class
 *
 * A single sound on a soundboard — either shared on a server
 * (`parent.type === "Server"`) or personal to the current account
 * (`parent.type === "Detached"`). Backed by the real `/soundboard/*`
 * backend routes, not by a message/attachment hack.
 */
export class SoundboardSound {
    #collection;
    id;
    /**
     * Construct SoundboardSound
     * @param collection Collection
     * @param id Sound Id
     */
    constructor(collection, id) {
        this.#collection = collection;
        this.id = id;
    }
    /**
     * Whether this object exists
     */
    get $exists() {
        return !!this.#collection.getUnderlyingObject(this.id).id;
    }
    /**
     * Time when this sound was created
     */
    get createdAt() {
        return new Date(decodeTime(this.id));
    }
    /**
     * Where this sound lives: a server's shared board, or the creator's
     * personal (Detached) board
     */
    get parent() {
        return this.#collection.getUnderlyingObject(this.id).parent;
    }
    /**
     * Id of whoever created this sound
     */
    get creatorId() {
        return this.#collection.getUnderlyingObject(this.id).creatorId;
    }
    /**
     * Creator of the sound, if known locally
     */
    get creator() {
        return this.#collection.client.users.get(this.creatorId);
    }
    /**
     * Display name
     */
    get name() {
        return this.#collection.getUnderlyingObject(this.id).name;
    }
    /**
     * Optional emoji shown alongside the name
     */
    get emoji() {
        return this.#collection.getUnderlyingObject(this.id).emoji;
    }
    /**
     * Autumn file id of the uploaded audio
     */
    get fileId() {
        return this.#collection.getUnderlyingObject(this.id).fileId;
    }
    /**
     * Playback volume, 0-200 (100 = normal)
     */
    get volume() {
        return this.#collection.getUnderlyingObject(this.id).volume;
    }
    /**
     * URL to download/play the sound's audio file
     */
    get url() {
        const autumnUrl = this.#collection.client.configuration?.features.autumn
            .url;
        return autumnUrl ? `${autumnUrl}/attachments/${this.fileId}` : undefined;
    }
    /**
     * Delete this sound. Creator or (for a Server-parented sound)
     * ManageCustomisation may delete; a Detached sound may only be deleted
     * by its creator.
     */
    async delete() {
        await this.#collection.deleteSound(this.id);
    }
}
